import { useMemo } from 'react';
import { noop } from 'lodash';

import { isValidCompleDateString } from '~/DateTimeUtils';
import useIdBasedPagination from '~/hooks/useIdBasedPagination';
import type {
  WorkflowExecutionItemIdsParams,
  WorkflowExecutionItemsByIdsParams,
  WorkflowExecutionModel,
} from '~/server_shared/openapi/data-contracts';

// Add type for execution summary data
interface ExecutionSummaryData {
  total_executions: number;
  status_counts: Record<string, number>;
}

const DEFAULT_ROWS_PER_PAGE = 25;
const FETCH_IDS_URL = '/api/v1/automation/workflow_engine/executions/organizations';
const FETCH_PAGE_DATA_URL = '/api/v1/automation/workflow_engine/executions/organizations';

interface UseWorkflowExecutionsPaginationParams {
  organizationId: number;
  initialRowsPerPage?: number;
  sortByColumn?: string;
  // Filtering parameters - using ID-based filtering
  workflowId?: string | null;
  ruleId?: string | null;
  status?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface WorkflowExecutionsPaginationResult {
  pagination: {
    page: number;
    setPage: (page: number) => void;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    totalCount: number;
    resultsTruncated: boolean;
    sortBy: string;
    setSortBy: (sortBy: string) => void;
    sortDirection: 'asc' | 'desc';
    setSortDirection: (direction: 'asc' | 'desc') => void;
  };
  data: WorkflowExecutionModel[];
  loading: boolean;
  error: string | null;
  reloadExecutions: () => Promise<void>;
  statusCounts: Record<string, number>;
  totalExecutions: number;
}

export const useWorkflowExecutionsPagination = ({
  organizationId,
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  sortByColumn = 'created_datetime',
  workflowId,
  ruleId,
  status,
  startDate,
  endDate,
}: UseWorkflowExecutionsPaginationParams): WorkflowExecutionsPaginationResult => {
  const fetchIdsParams = useMemo((): WorkflowExecutionItemIdsParams => {
    const params: WorkflowExecutionItemIdsParams = {};

    // Add sorting parameter
    if (sortByColumn) {
      params.sort_by = sortByColumn;
    }

    // Use ID-based filtering (backend already supports these fields)
    if (workflowId && workflowId.trim() !== '') {
      params.workflow_id = workflowId.trim();
    }
    if (ruleId && ruleId.trim() !== '') {
      params.rule_id = parseInt(ruleId.trim(), 10);
    }
    if (status && status.length > 0) {
      params.status = status;
    }
    // Only send date filters if they are valid and complete
    if (isValidCompleDateString(startDate)) {
      params.iso_datetime_from = startDate;
    }
    if (isValidCompleDateString(endDate)) {
      params.iso_datetime_to = endDate;
    }

    return params;
  }, [workflowId, ruleId, status, startDate, endDate, sortByColumn]);

  // Build the request payload for the page data endpoint
  // Note: IDs are handled automatically by useIdBasedPagination, so we omit them from the type
  const fetchPageDataParams = useMemo((): Omit<WorkflowExecutionItemsByIdsParams, 'ids'> => {
    return {
      // Add any additional parameters needed for the items_by_ids endpoint
      // For now, we don't need any additional parameters
    };
  }, []);

  const {
    pagination,
    pageResults: data,
    isLoading: loading,
    isError: error,
    reload: reloadExecutions,
    extraData,
  } = useIdBasedPagination<string, ExecutionSummaryData, WorkflowExecutionModel, unknown>({
    fetchIdsUrl: `${FETCH_IDS_URL}/${organizationId}/item_ids`,
    fetchPageDataUrl: `${FETCH_PAGE_DATA_URL}/${organizationId}/items_by_ids`,
    fetchIdsParams: fetchIdsParams as Record<string, unknown>,
    fetchPageDataParams: fetchPageDataParams as Record<string, unknown>,
    initialRowsPerPage,
  });

  // Extract status counts from extraData
  const statusCounts = extraData?.status_counts || {};
  const totalExecutions = extraData?.total_executions || 0;

  return {
    pagination: {
      page: pagination.page,
      setPage: pagination.setPage,
      rowsPerPage: pagination.rowsPerPage,
      setRowsPerPage: pagination.setRowsPerPage,
      totalCount: pagination.totalCount,
      resultsTruncated: pagination.resultsTruncated,
      sortBy: sortByColumn,
      // Sort state is managed by the parent component (WorkflowExecutionsTab)
      // This is intentionally a no-op as sorting is handled via sortByColumn prop
      setSortBy: () => noop,
      sortDirection: sortByColumn?.startsWith('-') ? 'desc' : 'asc',
      // Sort state is managed by the parent component (WorkflowExecutionsTab)
      // This is intentionally a no-op as sorting is handled via sortByColumn prop
      setSortDirection: () => noop,
    },
    data: data || [],
    loading,
    error: error ? 'Error loading executions' : null,
    reloadExecutions: () => {
      reloadExecutions();
      return Promise.resolve();
    },
    statusCounts,
    totalExecutions,
  };
};
